/** Horizontal scrolling category pill filter for the discover page. */
import { useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import type { CourseCategory } from '@formency/shared';

interface CategoryFilterProps {
  categories: CourseCategory[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}

export default function CategoryFilter({ categories, selectedId, onSelect }: CategoryFilterProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll the selected pill into view
  useEffect(() => {
    if (!scrollRef.current || !selectedId) return;
    const active = scrollRef.current.querySelector('[data-active="true"]');
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [selectedId]);

  if (categories.length === 0) return null;

  return (
    <div
      ref={scrollRef}
      className="flex gap-2 overflow-x-auto py-1"
      style={{ scrollbarWidth: 'none' }}
    >
      {/* All pill */}
      <FilterPill label="All" active={selectedId === null} onClick={() => onSelect(null)} />

      {categories.map((cat) => (
        <FilterPill
          key={cat.id}
          label={cat.name}
          active={selectedId === cat.id}
          onClick={() => onSelect(selectedId === cat.id ? null : cat.id)}
        />
      ))}
    </div>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-active={active}
      onClick={onClick}
      className="relative shrink-0 rounded-full px-4 py-1.5 font-medium transition-colors duration-200"
      style={{
        fontSize: 'var(--type-meta-size)',
        fontFamily: 'var(--font-sans)',
        color: active ? 'var(--color-accent-text)' : 'var(--color-text-muted)',
        background: 'transparent',
      }}
    >
      {/* Active pill background */}
      {active && (
        <motion.div
          layoutId="category-active-pill"
          className="absolute inset-0 rounded-full"
          style={{
            background: 'var(--color-accent-subtle)',
            border: '1px solid var(--color-accent-outline)',
          }}
          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
        />
      )}
      <span className="relative">{label}</span>
    </button>
  );
}
